import { MentionChipKind } from '@design/components/mention-chip/MentionChip';

export const MENTION_TYPES = [
  'entity',
  'theme',
  'list',
  'public-list',
  'insight',
  'insight-scenario',
  'insight-catalyst-company',
  'insight-catalyst-market',
  'insight-earnings-preview',
  'insight-earnings-recap',
] as const;
export type MentionType = (typeof MENTION_TYPES)[number];

const MENTION_TYPE_SET = new Set<string>(MENTION_TYPES);

export const isMentionType = (value: string): value is MentionType =>
  MENTION_TYPE_SET.has(value);

export const MAX_MENTIONS_PER_REQUEST = 100;

export type MentionTriggerChar = '@' | '#';

/** The trigger + search text `MentionComposerInput` currently wants results for. */
export interface MentionQuery {
  trigger: MentionTriggerChar;
  query: string;
}

export interface Mention {
  type: MentionType;
  id: string;
  name: string;
  label: string;
}

/**
 * Generic candidate shape the mention popup renders and inserts as a chip.
 * Domain-specific data (the full entity/theme record used by a consumer's
 * custom row/preview renderers) travels in `meta` — the composer itself
 * never reads it.
 */
export interface MentionCandidate {
  kind: MentionType;
  id: string;
  label: string;
  avatarUrl?: string;
  accentClassName?: string;
  meta?: unknown;
}

export const mentionTypeToChipKind = (type: MentionType): MentionChipKind => {
  if (type === 'list' || type === 'public-list') {
    return 'watchlist';
  }
  return type;
};

export const toMention = (
  type: string,
  id: string,
  label: string
): Mention | undefined =>
  id && label && isMentionType(type)
    ? { type, id, name: label, label }
    : undefined;

export const mentionCandidateToMention = (
  candidate: MentionCandidate
): Mention => ({
  type: candidate.kind,
  id: candidate.id,
  name: candidate.label,
  label: candidate.label,
});
